import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, ChevronDown,
  Phone, Loader2, FileText, Download, Eye,
  SlidersHorizontal, MessageCircle
} from 'lucide-react';
import { message } from 'antd';
import { maskPhone, formatDate } from '../utils/helpers';
import { invoiceApi, companyProfileApi,ordersApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import * as XLSX from 'xlsx';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import ExcelExportButton from '../components/ExcelExportButton';
import Pagination from '../components/Pagination';
import { ASSET_BASE_URL as ASSET_URL } from '../services/api';
// ─────────────────────────────────────────────────────────────────────────────
// InvoiceDetail Component — UI matches Image 2 (Aarogya India style)
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// InvoiceDetail Component — Exact match to reference image
// ─────────────────────────────────────────────────────────────────────────────
function InvoiceDetail({ invoice, onBack, onDownload, currentTheme }) {
  if (!invoice) return null;
  const d = invoice;

  return (
    <div className="space-y-4">

      {/* ── Top nav ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-full flex items-center justify-center border border-gray-200 hover:bg-gray-50 transition-colors"
            style={{ color: currentTheme.color }}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <FileText className="w-5 h-5" style={{ color: currentTheme.color }} />
            Invoice detail
          </h1>
        </div>
        <ExcelExportButton
          onClick={onDownload}
          label="DOWNLOAD"
        />
      </div>

      {/* ── Invoice Card ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

        {/* ══ HEADER ══ */}
        <div className="p-6 pb-5">

          {/* Row 1: Logo | TAX INVOICE | Tagline + Date */}
          <div className="flex items-start justify-between gap-6 mb-5">

            {/* LEFT: Logo + GSTIN/FSSAI */}
            <div className="flex flex-col gap-1 min-w-[200px]">
              {d.company?.logoUrl ? (
                <img
                  src={d.company.logoUrl}
                  alt="Company Logo"
                  className="h-16 object-contain object-left mb-1"
                />
              ) : (
                <div
                  className="text-3xl font-extrabold leading-tight mb-1"
                  style={{ color: currentTheme.color, fontFamily: 'Georgia, serif' }}
                >
                  {d.company?.name || 'Company'}
                </div>
              )}
              <p className="text-[11px] text-gray-500 font-medium">
                {d.company?.gstin ? `GSTIN: ${d.company.gstin}` : ''}
                {d.company?.gstin && d.company?.fssai ? ' | ' : ''}
                {d.company?.fssai ? `FSSAI: ${d.company.fssai}` : ''}
              </p>
            </div>

            {/* MIDDLE: TAX INVOICE + Invoice # */}
            <div className="flex flex-col gap-1 flex-1">
              <p className="text-2xl font-bold text-gray-800 tracking-wide">TAX INVOICE</p>
              <p className="text-sm text-gray-600">
                Invoice #:&nbsp;<span className="font-bold text-gray-800">{d.invoiceNo || '—'}</span>
              </p>
            </div>

            {/* RIGHT: Tagline + Date */}
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              {d.company?.tagLine && (
                <p className="text-sm font-bold" style={{ color: currentTheme.color }}>
                  {d.company.tagLine}
                </p>
              )}
              <p className="text-sm text-gray-500">
                Invoice date:&nbsp;
                <span className="font-semibold text-gray-800">{d.invoiceDate || '—'}</span>
              </p>
            </div>
          </div>

          {/* Row 2: Company Info | Billed To | Shipped To */}
          <div className="grid grid-cols-3 border border-gray-200 rounded-xl overflow-hidden">

            {/* Company address */}
            <div className="p-4 border-r border-gray-200 space-y-2">
              <div className="flex items-start gap-2">
                <span className="text-orange-400 text-base mt-0.5">📍</span>
                <p className="text-[12px] text-gray-700 leading-relaxed">
                  {d.company?.name}
                  {d.company?.address ? `, ${d.company.address}` : ''}
                </p>
              </div>
              {d.company?.phone && (
                <div className="flex items-center gap-2">
                  <span className="text-base" style={{ color: currentTheme.color }}>📞</span>
                  <p className="text-[12px] text-gray-700">{d.company.phone}</p>
                </div>
              )}
              {d.company?.email && (
                <div className="flex items-center gap-2">
                  <span className="text-base" style={{ color: currentTheme.color }}>✉️</span>
                  <p className="text-[12px] text-gray-700">{d.company.email}</p>
                </div>
              )}
            </div>

            {/* Billed To */}
            <div className="p-4 border-r border-gray-200 space-y-1.5">
              <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Billed to</p>
              <p className="text-sm font-bold text-gray-800">{d.billedTo?.name || '—'}</p>
              {d.billedTo?.phone && (
                <div className="flex items-center gap-2">
                  <span className="text-sm" style={{ color: currentTheme.color }}>📞</span>
                  <p className="text-[12px] text-gray-600">{d.billedTo.phone}</p>
                </div>
              )}
              {d.billedTo?.address && (
                <div className="flex items-start gap-2">
                  <span className="text-orange-400 text-sm mt-0.5">📍</span>
                  <p className="text-[12px] text-gray-500 leading-relaxed">{d.billedTo.address}</p>
                </div>
              )}
            </div>

            {/* Shipped To */}
            <div className="p-4 space-y-1.5">
              <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Shipped to</p>
              <p className="text-sm font-bold text-gray-800">{d.shippedTo?.name || '—'}</p>
              {d.shippedTo?.phone && (
                <div className="flex items-center gap-2">
                  <span className="text-sm" style={{ color: currentTheme.color }}>📞</span>
                  <p className="text-[12px] text-gray-600">{d.shippedTo.phone}</p>
                </div>
              )}
              {d.shippedTo?.address && (
                <div className="flex items-start gap-2">
                  <span className="text-orange-400 text-sm mt-0.5">📍</span>
                  <p className="text-[12px] text-gray-500 leading-relaxed">{d.shippedTo.address}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ══ ITEMS TABLE ══ */}
        <div className="overflow-x-auto border-t border-gray-100">
          <table className="w-full">
            <thead>
              <tr style={{ backgroundColor: currentTheme.color + '18' }}>
                {['S. No.', 'Product', 'Unit price', 'Quantity', 'Discount', 'Total Price'].map(h => (
                  <th
                    key={h}
                    className="px-5 py-3 text-left text-xs font-bold tracking-wide"
                    style={{ color: currentTheme.color }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(d.items || []).map((item, idx) => (
                <tr key={idx} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4 text-sm text-gray-600">{idx + 1}</td>
                  <td className="px-5 py-4 text-sm font-semibold text-gray-800">
                    {item.productName || item.product || '—'}
                  </td>
                  <td className="px-5 py-4 text-sm font-bold" style={{ color: currentTheme.color }}>
                    ₹ {Number(item.unitPrice || 0).toFixed(2)}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-700">
                    {item.quantity || item.qty || 1}
                  </td>
                  <td className="px-5 py-4 text-sm font-semibold" style={{ color: currentTheme.color }}>
                    ₹ {Number(item.discount || 0).toFixed(2)}
                  </td>
                  <td className="px-5 py-4 text-sm font-bold" style={{ color: currentTheme.color }}>
                    ₹ {Number(item.totalPrice || item.total || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
              {(!d.items || d.items.length === 0) && (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-sm text-gray-400">
                    No items found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ══ FOOTER: Terms + Signature LEFT | Totals RIGHT ══ */}
        <div className="grid grid-cols-2 border-t border-gray-100">

          {/* Terms + Signature */}
          <div className="p-6 border-r border-gray-100">
            <p className="text-sm font-bold text-gray-700 mb-3">Terms and Conditions:</p>
            <ol className="list-decimal list-inside space-y-2">
              {(d.terms || []).map((t, i) => (
                <li key={i} className="text-[12px] text-gray-500 leading-relaxed">{t}</li>
              ))}
            </ol>

            {/* Signature block */}
            <div className="mt-8 flex flex-col gap-1">
              <p className="text-[12px] text-gray-500">
                For {d.company?.name || 'Company'}
              </p>
              <div className="h-16 flex items-center">
                {d.company?.signatureUrl ? (
                  <img
                    src={d.company.signatureUrl}
                    alt="Authorized Signatory"
                    className="h-14 object-contain"
                  />
                ) : (
                  <span
                    className="text-4xl italic text-gray-300"
                    style={{ fontFamily: 'cursive' }}
                  >
                    ~~~~
                  </span>
                )}
              </div>
              <p className="text-[12px] text-gray-500">Authorized Signatory</p>
            </div>
          </div>

          {/* Totals */}
          <div className="p-6 flex flex-col justify-end">
            <div className="space-y-0 w-full max-w-sm ml-auto">

              {/* Subtotal */}
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-sm text-gray-600">Subtotal:</span>
                <span className="text-sm font-bold" style={{ color: currentTheme.color }}>
                  ₹ {Number(d.subTotal || d.subtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>

              {/* Advance payable */}
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-sm text-gray-600">Advance payable</span>
                <span className="text-sm font-semibold text-gray-700">
                  ₹ {Number(d.advancePayable || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>

              {/* Taxable Amount */}
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-sm text-gray-600">
                  Taxable Amount included
                  <span className="block text-[11px] text-gray-400">
                    (GST @{d.gstPercentage || 18}%)
                  </span>
                </span>
                <span className="text-base font-bold" style={{ color: currentTheme.color }}>
                  ₹ {Number(d.grandTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>

              {/* To Pay */}
              <div className="flex justify-between items-center pt-4">
                <span className="text-base font-bold text-gray-700">To pay:</span>
                <span
                  className="text-2xl font-extrabold"
                  style={{ color: currentTheme.color }}
                >
                  ₹ {Number(d.pendingAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionDropdown({ onViewInvoice, onDownload, currentTheme }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative flex justify-center" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
        title="Actions"
      >
        <Eye className="w-4 h-4" style={{ color: currentTheme.color }} />
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-30 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden min-w-[170px]">
          <button
            onClick={() => { onViewInvoice(); setOpen(false); }}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <FileText className="w-3.5 h-3.5 text-gray-400" /> Invoice details
          </button>
          <div className="border-t border-gray-100" />
          <button
            onClick={() => { onDownload(); setOpen(false); }}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Download className="w-3.5 h-3.5 text-gray-400" /> Download invoice
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Status Badge
// ─────────────────────────────────────────────────────────────────────────────
function StatusBadge({ status, isDark }) {
  const styleMap = {
    PROCESSING:       isDark ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-amber-50 text-amber-600 border-amber-200',
    READY_TO_SHIP:    isDark ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-amber-50 text-amber-600 border-amber-200',
    DISPATCHED:       isDark ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-blue-50 text-blue-600 border-blue-200',
    IN_TRANSIT:       isDark ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-blue-50 text-blue-600 border-blue-200',
    OUT_FOR_DELIVERY: isDark ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' : 'bg-purple-50 text-purple-600 border-purple-200',
    DELIVERED:        isDark ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-green-50 text-green-600 border-green-200',
    CANCELLED:        isDark ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-red-50 text-red-500 border-red-200',
    RTO:              isDark ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-red-50 text-red-500 border-red-200',
    NDR:              isDark ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' : 'bg-orange-50 text-orange-600 border-orange-200',
    HOLD:             isDark ? 'bg-white/10 text-gray-400 border-white/20' : 'bg-gray-100 text-gray-500 border-gray-200',
    GENERATED:        isDark ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-green-50 text-green-600 border-green-200',
    SENT:             isDark ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-blue-50 text-blue-600 border-blue-200',
    PAID:             isDark ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-green-50 text-green-700 border-green-200',
  };
  const labelMap = {
    PROCESSING: 'Processing', READY_TO_SHIP: 'Ready to ship',
    DISPATCHED: 'Dispatched', IN_TRANSIT: 'In Transit',
    OUT_FOR_DELIVERY: 'Out for Delivery', DELIVERED: 'Delivered',
    CANCELLED: 'Cancelled', RTO: 'RTO', NDR: 'NDR', HOLD: 'On Hold',
    GENERATED: 'Generated', SENT: 'Sent', PAID: 'Paid',
  };
  return (
    <span className={`px-2.5 py-1 rounded-md text-[10px] font-semibold border ${styleMap[status] || (isDark ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-amber-50 text-amber-600 border-amber-200')}`}>
      {labelMap[status] || status || '—'}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main BillingList Component
// ─────────────────────────────────────────────────────────────────────────────
function BillingList() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
const [totalInvoices, setTotalInvoices] = useState(0);
  // Invoice view state
  const [invoiceView, setInvoiceView] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Checkbox selection
  const [selectedRows, setSelectedRows] = useState(new Set());

  const toggleRow = (id) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

const fetchData = async (page = 1, search = searchTerm, size = pageSize) => {
  try {
    setLoading(true);
    setError(null);

    const [billingData, profileData] = await Promise.allSettled([
      invoiceApi.getAllInvoices({
        page: page - 1,   // backend 0-based
        size: size,
        search: search?.trim() || undefined,
      }),
      companyProfileApi.getDefaultProfile(),
    ]);

    const rawBilling = billingData.status === 'fulfilled' ? billingData.value : null;
    let list = [];
    let total = 0;

    if (Array.isArray(rawBilling)) {
      list = rawBilling;
      total = rawBilling.length;
    } else if (rawBilling && typeof rawBilling === 'object') {
      list  = rawBilling.content || rawBilling.data || [];
      total = rawBilling.totalElements || rawBilling.total || list.length;
    }

    setInvoices(list);
    setTotalInvoices(total);

    if (profileData.status === 'fulfilled' && profileData.value) {
      setCompanyProfile(profileData.value);
    } else {
      try {
        const profiles = await companyProfileApi.getActiveProfiles();
        const arr = Array.isArray(profiles) ? profiles : profiles?.content || profiles?.data || [];
        if (arr.length > 0) setCompanyProfile(arr[0]);
      } catch (_) {}
    }
  } catch (err) {
    console.error('Error fetching billing data:', err);
    setError('Failed to load billing list.');
  } finally {
    setLoading(false);
  }
};
// useEffect update करें:
useEffect(() => { fetchData(1); }, []);  // sirf mount pe

// Search handler — debounce ke saath:
const handleSearchChange = (val) => {
  setSearchTerm(val);
  setCurrentPage(1);
  fetchData(1, val, pageSize);
};

// PageSize handler:
const handlePageSizeChange = (newSize) => {
  setPageSize(newSize);
  setCurrentPage(1);
  fetchData(1, searchTerm, newSize);
};
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return invoices;
    const t = searchTerm.toLowerCase();
    return invoices.filter(item =>
      (item.customerName && item.customerName.toLowerCase().includes(t)) ||
      (item.billedToName && item.billedToName.toLowerCase().includes(t)) ||
      (item.orderId && String(item.orderId).toLowerCase().includes(t)) ||
      (item.invoiceNumber && item.invoiceNumber.toLowerCase().includes(t))
    );
  }, [invoices, searchTerm]);

const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
const safePage = Math.min(currentPage, totalPages);
const startIndex = (safePage - 1) * pageSize;
const pageData = filteredData.slice(startIndex, startIndex + pageSize);

  // Checkbox helpers — after pageData
  const toggleAll = () => {
    if (selectedRows.size === pageData.length && pageData.length > 0) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(pageData.map(i => i.id)));
    }
  };
  const isAllSelected = invoices.length > 0 && invoices.every(i => selectedRows.has(i.id));

  const handleBulkDownload = async () => {
    if (selectedRows.size === 0) return;
    try {
      message.loading({ content: 'Downloading and zipping invoices...', key: 'bulk', duration: 0 });

      const JSZip = (await import('jszip')).default; // Dynamic import
      const zip = new JSZip();

      const idsArray = [...selectedRows];

      // Download selected PDFs sequentially to avoid overwhelming the server/browser network limits
      let successCount = 0;
      for (let i = 0; i < idsArray.length; i++) {
        const id = idsArray[i];
        try {
          message.loading({ content: `Downloading invoice ${i + 1} of ${idsArray.length}...`, key: 'bulk', duration: 0 });

          // Look up in pageData first, then fallback to full invoices list
          const selectedOrder = invoices.find(item => item.id === id);

          // CRITICAL: downloadByOrderId needs the ACTUAL orderId, not the row's primary key (invoiceId)
          const targetOrderId = selectedOrder?.orderId || id;

          // Generate a strictly unique file name to avoid zip file overwriting
          const invoiceNum = (selectedOrder && selectedOrder.invoiceNumber)
                                ? selectedOrder.invoiceNumber
                                : `Order_${targetOrderId}`;
          const uniqueId = Math.floor(Math.random() * 10000);
          const fileName = `Invoice_${invoiceNum}_${uniqueId}.pdf`;

          const blob = await invoiceApi.downloadByOrderId(targetOrderId);
          zip.file(fileName, blob);
          successCount++;
        } catch (error) {
          console.error(`Failed to download invoice for ID ${id}:`, error);
          // Show a discrete warning for this specific failure
          message.warning({ content: `Failed: ${error.message}`, key: `err_${id}`, duration: 5 });
        }
      }

      if (successCount === 0) {
        message.error({ content: 'Failed to download any invoices. Check server.', key: 'bulk' });
        return;
      }

      message.loading({ content: 'Zipping files together...', key: 'bulk', duration: 0 });

      // Generate ZIP and download
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoices_Bulk_${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      message.success({ content: `${selectedRows.size} invoice(s) downloaded as ZIP!`, key: 'bulk' });
      setSelectedRows(new Set(invoices.map(i => i.id)));
    } catch (err) {
      console.error('Bulk download error:', err);
      message.error({ content: 'Bulk download failed.', key: 'bulk' });
    }
  };
const handleSingleDownload = async (item) => {
  try {
    message.loading({ content: 'Downloading...', key: `dl_${item.id}` });

    // CRITICAL: Ensure we use orderId if available
    const targetOrderId = item.orderId || item.id;
    const blob = await invoiceApi.downloadByOrderId(targetOrderId);

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice_${item.invoiceNumber || targetOrderId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    message.success({ content: 'Invoice downloaded!', key: `dl_${item.id}` });
  } catch (err) {
    console.error('Single download error:', err);
    message.error({
      content: `Download failed: ${err.message}. Please check if the invoice exists on the server.`,
      key: `dl_${item.id}`,
      duration: 10
    });
  }
};


const handleExportExcel = () => {
  try {
    message.loading({ content: 'Preparing Excel file...', key: 'excel' });

    const dataToExport = filteredData.map(item => ({
      'Order Date': formatDate(item.createdAt || item.orderDate),
      'Invoice #': item.invoiceNumber || item.orderId || '—',
      'Customer Name': item.billedToName || item.customerName || '—',
      'Phone': item.billedToPhone || item.contactNumber || '',
      'Product': item.invoiceItems?.[0]?.productName || item.orderItems?.[0]?.productName || '—',
      'Amount': Number(item.grandTotal || item.totalAmount || 0),
      'Pay Mode': item.paymentMode || item.paymentType || '—',
      'Campaign': item.campaignName || item.teamName || item.leadSource || '—',
      'Status': item.invoiceStatus || item.status || '—'
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Billing List");

    // Fix column widths
    const wscols = [
      { wch: 20 }, // Order Date
      { wch: 15 }, // Invoice #
      { wch: 25 }, // Customer Name
      { wch: 15 }, // Phone
      { wch: 30 }, // Product
      { wch: 12 }, // Amount
      { wch: 15 }, // Pay Mode
      { wch: 20 }, // Campaign
      { wch: 15 }  // Status
    ];
    worksheet['!cols'] = wscols;

    XLSX.writeFile(workbook, `Billing_List_${new Date().toISOString().split('T')[0]}.xlsx`);

    message.success({ content: 'Excel file downloaded!', key: 'excel' });
  } catch (err) {
    console.error('Excel export error:', err);
    message.error({ content: 'Failed to export Excel file.', key: 'excel' });
  }
};



  // Build invoice object for InvoiceDetail view
  // Uses real data from /invoice/billing-list + company profile
  const IMAGE_BASE_URL = `${ASSET_URL}/uploads/`;
 const handleViewInvoice = async (item) => {
  let fullInvoice = item;
  try {
    const detail = await invoiceApi.getInvoiceByOrderId(item.id);
    if (detail) fullInvoice = detail;
  } catch (_) {}

  const cp = fullInvoice.companyProfile || companyProfile;

  // instructionNotes se terms nikalo
  const terms = Array.isArray(cp?.instructionNotes) && cp.instructionNotes.length > 0
    ? cp.instructionNotes
        .filter(n => n.instructionType === 'Term and conditions')
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
        .map(n => n.instruction)
    : [
        "Refunds/returns follow the company's policy. Opened or used products are not eligible for return unless defective.",
        'Ayurvedic products are wellness supplements; results may vary.',
        'Placing an order means acceptance of these terms',
      ];

  const invoice = {
    // ── Invoice meta ──
    invoiceNo:   fullInvoice.invoiceNumber,
    invoiceDate: formatDate(fullInvoice.invoiceDate || fullInvoice.createdAt),

    // ── Totals (exact API field names) ──
    subTotal:       fullInvoice.subtotal      || 0,
    advancePayable: fullInvoice.advancePayable || 0,
    grandTotal:     fullInvoice.totalAmount   || 0,
    gstPercentage:  fullInvoice.gstPercentage || 18,
    gstAmount:      fullInvoice.gstAmount     || 0,
    // pendingAmount = totalAmount - advancePayable
    pendingAmount:  (fullInvoice.totalAmount || 0) - (fullInvoice.advancePayable || 0),

    // ── Company (exact API field names from companyProfile object) ──
    company: {
      name:          cp?.companyName    || '',
      // Logo/signature/UPI URLs — prepend base URL if not already full URL
      logoUrl:       cp?.companyLogoUrl
                       ? cp.companyLogoUrl.startsWith('http')
                           ? cp.companyLogoUrl
                           : IMAGE_BASE_URL + cp.companyLogoUrl
                       : null,
      signatureUrl:  cp?.signatureUrl
                       ? cp.signatureUrl.startsWith('http')
                           ? cp.signatureUrl
                           : IMAGE_BASE_URL + cp.signatureUrl
                       : null,
      upiScannerUrl: cp?.upiScannerUrl
                       ? cp.upiScannerUrl.startsWith('http')
                           ? cp.upiScannerUrl
                           : IMAGE_BASE_URL + cp.upiScannerUrl
                       : null,
      gstin:         cp?.gstNumber      || '',
      fssai:         cp?.fssaiNumber    || '',
      address:       cp?.locality       || '',
      phone:         cp?.contactNumber  || '',
      email:         cp?.email          || '',
      tagLine:       cp?.tagLine        || '',
    },

    // ── Billed to (exact API field names) ──
    billedTo: {
      name:    fullInvoice.billedToName    || '',
      phone:   fullInvoice.billedToPhone   || '',
      address: fullInvoice.billedToAddress || '',
    },

    // ── Shipped to (exact API field names) ──
    shippedTo: {
      name:    fullInvoice.shippedToName    || '',
      phone:   fullInvoice.shippedToPhone   || '',
      address: fullInvoice.shippedToAddress || '',
    },

    // ── Items — direct from invoiceItems array ──
    // fields: id, productName, quantity, unitPrice, discount, totalPrice, serialNumber
    items: fullInvoice.invoiceItems || [],

    terms,
  };

  setSelectedInvoice(invoice);
  setInvoiceView(true);
};



  // Render InvoiceDetail
  if (invoiceView && selectedInvoice) {
    return (
      <InvoiceDetail
        invoice={selectedInvoice}
        onBack={() => { setInvoiceView(false); setSelectedInvoice(null); }}
        onDownload={() => handleSingleDownload(selectedInvoice)}
        currentTheme={currentTheme}
      />
    );
  }

  return (
    <div className={`space-y-4 p-4 min-h-screen ${isDark ? 'bg-[#0f172a]' : ''}`}>

      {/* ── Page Title ── */}
      <div className="flex items-center justify-between">
        <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
          Billing list&nbsp;
          <span className="text-gray-400 font-normal text-base">({filteredData.length})</span>
        </h1>
        <div className="flex items-center gap-2">
          {/* <button className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors">
            <SlidersHorizontal className="w-4 h-4 text-gray-500" />
          </button> */}
          <ExcelExportButton
            onClick={handleExportExcel}
            label="DOWNLOAD EXCEL"
          />
        </div>
      </div>

      {/* ── Bulk Selection Bar ── */}
      {selectedRows.size > 0 && (
        <div
          className="flex items-center justify-between px-4 py-2.5 rounded-xl border shadow-sm"
          style={{ backgroundColor: currentTheme.color + '12', borderColor: currentTheme.color + '40' }}
        >
          <span className="text-sm font-semibold" style={{ color: currentTheme.color }}>
            {selectedRows.size} order{selectedRows.size > 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedRows(new Set())}
              className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg bg-white transition-colors"
            >
              Clear
            </button>
            <ExcelExportButton
              onClick={handleBulkDownload}
              label={`DOWNLOAD INVOICE${selectedRows.size > 1 ? 'S' : ''}`}
            />
          </div>
        </div>
      )}

      {/* ── Search + Page Size ── */}
      <div className="flex items-center gap-3">
        <ExpandableSearchBar
          searchTerm={searchTerm}
         onSearchChange={handleSearchChange}
          placeholder="Search by order id or customer name"
          className="h-10"
        />
        <div className="relative">
          <select
            value={pageSize}
           onChange={e => handlePageSizeChange(Number(e.target.value))}
            className="appearance-none bg-white border-2 border-gray-200 rounded-full pl-4 pr-8 py-2 text-sm focus:outline-none cursor-pointer"
          >
            <option value={10}>10 Orders</option>
            <option value={20}>20 Orders</option>
            <option value={50}>50 Orders</option>
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* ── Table ── */}
      <div className={`${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'} rounded-xl shadow-sm overflow-hidden border`}>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin mb-4" style={{ color: currentTheme.color }} />
            <p className="text-gray-500 text-sm">Loading billing list...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20">
            <p className="text-red-500 mb-4 text-sm">{error}</p>
            <button onClick={fetchData} className="px-4 py-2 text-white rounded-lg text-sm" style={{ backgroundColor: currentTheme.color }}>
              Retry
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-white" style={{ backgroundColor: currentTheme.color }}>
                    <th className="px-3 py-2.5 w-12 text-center">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={toggleAll}
                        className="w-3.5 h-3.5 rounded cursor-pointer"
                        style={{ accentColor: '#ffffff' }}
                        title="Select all"
                      />
                    </th>
                    {['Order Date', 'Invoice #', 'Name', 'Product', 'Amount', 'Pay Mode', 'Campaign', 'Status', 'Action'].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                  {pageData.map((item) => (
                    <tr
                      key={item.id}
                      className="transition-colors"
                      style={{ backgroundColor: selectedRows.has(item.id) ? (isDark ? `${currentTheme.color}30` : `${currentTheme.color}12`) : undefined }}
                      onMouseEnter={e => { if (!selectedRows.has(item.id)) e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.02)' : '#f9fafb'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = selectedRows.has(item.id) ? (isDark ? `${currentTheme.color}30` : `${currentTheme.color}12`) : ''; }}
                    >
                      {/* Checkbox */}
                      <td className="px-3 py-2.5 text-center">
                        <input
                          type="checkbox"
                          checked={selectedRows.has(item.id)}
                          onChange={() => toggleRow(item.id)}
                          className="w-3.5 h-3.5 rounded cursor-pointer"
                          style={{ accentColor: currentTheme.color }}
                        />
                      </td>

                      {/* Order Date */}
                      <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">
                        {formatDate(item.createdAt || item.orderDate)}
                      </td>

                      {/* Invoice # */}
                      <td className="px-3 py-2.5 text-xs font-medium ${isDark ? 'text-white/80' : 'text-gray-800'}">
                        {item.invoiceNumber || item.orderId || '—'}
                      </td>

                      {/* Name + Phone + WhatsApp */}
                      <td className="px-3 py-2.5">
                        <div className="flex flex-col gap-0.5">
                          <span className={`text-xs font-semibold ${isDark ? 'text-white/90' : 'text-gray-800'}`}>
                            {item.billedToName || item.customerName || '—'}
                          </span>
                          <div className="flex items-center gap-1.5">
                            <Phone className="w-3 h-3 text-gray-400" />
                            <span className="text-[10px] text-gray-500">
                              {maskPhone(item.billedToPhone || item.contactNumber || '')}
                            </span>
                            <span className="w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#25D366' }}>
                              <MessageCircle className="w-2.5 h-2.5 text-white" />
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Product — first item from invoiceItems */}
                      <td className="px-3 py-2.5 text-xs text-gray-700 max-w-[140px]">
                        <span className="line-clamp-2">
                          {item.invoiceItems?.[0]?.productName || item.orderItems?.[0]?.productName || '—'}
                        </span>
                      </td>

                      {/* Amount — grandTotal */}
                      <td className="px-3 py-2.5">
                        <span className={`text-sm font-extrabold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                          ₹ {Number(item.grandTotal || item.totalAmount || 0).toLocaleString('en-IN')}
                        </span>
                      </td>

                      {/* Pay Mode */}
                      <td className="px-3 py-2.5 text-xs text-gray-700 font-medium">
                        {item.paymentMode || item.paymentType || '—'}
                      </td>

                      {/* Campaign */}
                      <td className="px-3 py-2.5 text-xs text-gray-700">
                        {item.campaignName || item.teamName || item.leadSource || '—'}
                      </td>

                      {/* Status */}
                      <td className="px-3 py-2.5">
                        <StatusBadge status={item.invoiceStatus || item.status} isDark={isDark} />
                      </td>

                      {/* Action */}
                      <td className="px-3 py-2.5">
                        <ActionDropdown
                          currentTheme={currentTheme}
                          onViewInvoice={() => handleViewInvoice(item)}
                          onDownload={() => handleSingleDownload(item)}

                        />
                      </td>
                    </tr>
                  ))}

                  {pageData.length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-3 py-16 text-center text-gray-500">
                        <p className="text-sm font-medium">No invoices found</p>
                        <p className="text-xs text-gray-400 mt-1">Try adjusting your search</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ── Pagination ── */}
           <Pagination
  currentPage={currentPage}
  totalItems={totalInvoices}
  itemsPerPage={pageSize}
  onPageChange={(page) => {
    setCurrentPage(page);
    fetchData(page, searchTerm, pageSize);
  }}
/>
          </>
        )}
      </div>
    </div>
  );
}

export default BillingList;